import { createInstance, dateReviver } from '@/api';
import {
    CreateProductSpecificationDto,
    FeatureCatalogOptionsDto,
    ProductSpecificationClient,
    ProductSpecificationDetailDto,
    UpdateProductSpecificationDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';
import downloadBlob from '@/helpers/download.helpers';

export interface ProductSpecificationStoreState {
    items?: { key: string; value: ProductSpecificationDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductSpecificationClient('', createInstance(), dateReviver);

export const useProductSpecificationStore = defineStore('configuration-product-specification-store', {
    state: (): ProductSpecificationStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): ProductSpecificationDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${definitionId}`;

            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productspecification.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(definitionId: number, dto: CreateProductSpecificationDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('productspecification.detail.success.create.title'));
                this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('productspecification.detail.error.create.title'), error);
            } finally {
                this.reloading = false;
                if (!hasError) {
                    useProductProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, specificationId: number, dto: UpdateProductSpecificationDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(specificationId, dto);
                useAlertStore().setSuccess(i18n.global.t('productspecification.detail.success.update.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('productspecification.detail.error.update.title'), error);
            } finally {
                this.reloading = false;
                if (!hasError) {
                    useProductProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(definitionId: number, specificationId: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(specificationId);
                useAlertStore().setSuccess(i18n.global.t('productspecification.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productspecification.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        async generateFeatureCatalog(id: number, featureCatalogOptions: FeatureCatalogOptionsDto): Promise<void> {
            try {
                const file = await client.generateFeatureCatalog(id, featureCatalogOptions);

                return new Promise((resolve) => {
                    downloadBlob(file.data, file.fileName, () => {
                        resolve();
                    });
                });
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productspecification.detail.error.generateFeatureCatalog.title'),
                    error,
                    false
                );
            }
        },
        async hasUniqueSchema(schema: string, dataConnectionCode?: string | null): Promise<boolean | null> {
            try {
                const response = await client.hasUniqueSchema(schema, dataConnectionCode);
                return response;
            } catch (error) {
                return true;
            }
        },
        clear(definitionId: number) {
            const key = `definition-${definitionId}`;

            const specifications = this.items.find((x) => x.key === key)?.value;

            if (specifications) {
                specifications.forEach((specification) => {
                    // useProductEntityStore().clear(specification.id);
                });
            }

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
